export const preventAriaFocusIssue = () => {
  let isRunning = false;
  
  const fixAriaIssues = () => {
    if (isRunning) return;
    isRunning = true;
    
    try {
      // Handle incorrect aria-modal on popovers (should only be on true modals)
      const popoversWithModal = window.document.querySelectorAll('ion-popover[aria-modal="true"]');
      popoversWithModal.forEach((popover) => {
        popover.removeAttribute('aria-modal');
        console.log('ARIA: Removed incorrect aria-modal from popover');
      });
      
      // Fix aria-hidden elements that have tabindex (the main issue)
      // But exclude action sheets that are open - they should not have aria-hidden at all
      const ariaHiddenWithTabindex = window.document.querySelectorAll('[aria-hidden="true"][tabindex]:not([tabindex="-1"]):not(ion-action-sheet[is-open="true"])');
      ariaHiddenWithTabindex.forEach((element) => {
        element.setAttribute('tabindex', '-1');
        console.log('ARIA: Fixed aria-hidden element with tabindex:', element.tagName, element.className);
      });

      // Special handling for action sheets: remove aria-hidden when they're open
      const openActionSheets = window.document.querySelectorAll('ion-action-sheet[aria-hidden="true"][is-open="true"]');
      openActionSheets.forEach((actionSheet) => {
        actionSheet.removeAttribute('aria-hidden');
        console.log('ARIA: Removed aria-hidden from open action sheet:', actionSheet.id || actionSheet.className);
      });
      
      // Special handling for alerts: remove aria-hidden if they contain focused elements or have pointer events
      const alertsWithAriaHidden = window.document.querySelectorAll('ion-alert[aria-hidden="true"]');
      alertsWithAriaHidden.forEach((alert) => {
        // Check if alert is actually open (has pointer events or contains focused elements)
        const computedStyle = window.getComputedStyle(alert);
        const hasFocusedElement = alert.contains(document.activeElement) || 
                                 alert.querySelector(':focus') ||
                                 alert.querySelector('button.alert-button');
        
        if (computedStyle.pointerEvents !== 'none' || hasFocusedElement) {
          alert.removeAttribute('aria-hidden');
          console.log('ARIA: Removed aria-hidden from active alert:', alert.id || alert.className);
        }
      });
      
      // Also fix focusable elements inside aria-hidden containers (excluding open action sheets and active alerts)
      const hiddenElements = window.document.querySelectorAll('[aria-hidden="true"]:not(ion-action-sheet[is-open="true"]):not(ion-alert) [tabindex]:not([tabindex="-1"])');
      hiddenElements.forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
      
      // Fix progressbar elements without accessible names
      const progressbars = window.document.querySelectorAll('[role="progressbar"]:not([aria-label]):not([aria-labelledby]):not([title])');
      progressbars.forEach((progressbar) => {
        if (progressbar.tagName === 'ION-PROGRESS-BAR') {
          progressbar.setAttribute('aria-label', 'Loading progress');
        } else if (progressbar.tagName === 'ION-SPINNER') {
          progressbar.setAttribute('aria-label', 'Loading');
        } else {
          progressbar.setAttribute('aria-label', 'Progress indicator');
        }
        console.log('ARIA: Added accessible name to progressbar:', progressbar.tagName);
      });
      
      // Fix router outlets and other containers that shouldn't have aria-hidden when they contain focused elements
      const problematicHiddenContainers = window.document.querySelectorAll('ion-router-outlet[aria-hidden="true"], ion-nav[aria-hidden="true"], ion-tabs[aria-hidden="true"], ion-popover[aria-hidden="true"], ion-modal[aria-hidden="true"], ion-action-sheet[aria-hidden="true"][is-open="true"], ion-alert[aria-hidden="true"]');
      problematicHiddenContainers.forEach((container) => {
        // Check if this container or its children have focus or are focusable
        const hasFocusedElement = container.contains(document.activeElement) || 
                                 container.querySelector(':focus') ||
                                 container.querySelector('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"]), ion-radio, ion-checkbox');
        
        // For alerts, also check pointer-events to see if they're actually visible
        const isAlert = container.tagName === 'ION-ALERT';
        const isVisible = isAlert ? window.getComputedStyle(container).pointerEvents !== 'none' : true;
        
        if (hasFocusedElement || (isAlert && isVisible)) {
          container.removeAttribute('aria-hidden');
          console.log('ARIA: Removed aria-hidden from container with focusable content:', container.tagName);
        }
      });

      // Special handling for ion-page-hidden elements that contain focused elements
      const hiddenPages = window.document.querySelectorAll('.ion-page-hidden[aria-hidden="true"], .ion-page.ion-page-hidden[aria-hidden="true"]');
      hiddenPages.forEach((page) => {
        const hasFocusedElement = page.contains(document.activeElement) || page.querySelector(':focus');
        if (hasFocusedElement) {
          // Instead of removing aria-hidden, blur the focused elements
          const focusedElements = page.querySelectorAll(':focus, button:focus, input:focus, select:focus, textarea:focus');
          focusedElements.forEach((el) => {
            if (el instanceof HTMLElement && el.blur) {
              el.blur();
              console.log('ARIA: Blurred focused element in hidden page:', el.tagName, el.className);
            }
          });
          
          // Also blur if the page itself has focus
          if (page instanceof HTMLElement && page === document.activeElement && page.blur) {
            page.blur();
            console.log('ARIA: Blurred hidden page itself');
          }
        }
      });
      
      hiddenElements.forEach((element) => {
        // Special handling for open modals/overlays - they should NOT have aria-hidden
        if (element.getAttribute('is-open') === 'true' || 
            element.classList.contains('popover-open') ||
            element.querySelector('.popover-open')) {
          
          // If it's an open modal/overlay with aria-hidden, remove aria-hidden
          if (element.tagName === 'ION-MODAL' || 
              element.tagName === 'ION-POPOVER' || 
              element.tagName === 'ION-ACTION-SHEET' ||
              element.tagName === 'ION-ALERT' ||
              element.classList.contains('modal-sheet') ||
              element.id?.includes('ion-overlay')) {
            element.removeAttribute('aria-hidden');
            console.log('ARIA: Removed aria-hidden from open modal/overlay:', element.tagName);
          }
          return; // Skip further processing
        }
        
        // Special case: ION-ROUTER-OUTLET should NEVER have aria-hidden
        if (element.tagName === 'ION-ROUTER-OUTLET') {
          element.removeAttribute('aria-hidden');
          console.log('ARIA: Removed aria-hidden from router outlet');
          return; // Don't blur focused elements in router outlet
        }
        
        // Process all other Ionic components and modals
        if (element.tagName === 'ION-TAB' ||           
            element.tagName === 'ION-TABS' ||          
            element.tagName === 'ION-ACTION-SHEET' ||
            element.tagName === 'ION-MODAL' ||
            element.tagName === 'ION-POPOVER' ||
            element.tagName === 'ION-ALERT' ||
            element.tagName === 'ION-TOAST' ||
            element.classList.contains('ion-page') ||
            element.classList.contains('ion-page-hidden') ||
            element.classList.contains('tab-hidden') ||
            element.classList.contains('modal-sheet') ||
            element.id?.includes('ion-overlay')) {
          
          // Blur any focused elements inside
          const focusedInside = element.querySelectorAll(':focus');
          focusedInside.forEach((el) => {
            if (el instanceof HTMLElement && el.blur) {
              el.blur();
            }
          });
          
          // Also check if the element itself has focus
          if (element instanceof HTMLElement && element === document.activeElement && element.blur) {
            element.blur();
          }
        }
      });
    } catch (error) {
      console.warn('ARIA fix error:', error);
    } finally {
      isRunning = false;
    }
  };

  // Create multiple observers for different scenarios
  const mutationObserver = new MutationObserver(fixAriaIssues);
  
  // Start observing
  mutationObserver.observe(window.document.body, {
    attributes: true,
    attributeFilter: ['aria-hidden', 'is-open', 'aria-modal'],
    subtree: true,
    childList: true
  });

  // Also run on focus events
  const focusHandler = () => {
    // Immediate check for focused elements in hidden pages
    const focusedElement = document.activeElement;
    if (focusedElement) {
      const hiddenPage = focusedElement.closest('.ion-page-hidden[aria-hidden="true"], .ion-page.ion-page-hidden[aria-hidden="true"]');
      if (hiddenPage && focusedElement instanceof HTMLElement && focusedElement.blur) {
        focusedElement.blur();
        console.log('ARIA: Immediately blurred focused element in hidden page on focus event:', focusedElement.tagName);
      }
    }
    
    setTimeout(fixAriaIssues, 10);
  };
  
  // Modal event handlers
  const modalHandler = () => {
    setTimeout(fixAriaIssues, 50); // Slightly longer delay for modal events
  };
  
  document.addEventListener('focusin', focusHandler, true);
  document.addEventListener('focusout', focusHandler, true);
  
  // Listen for Ionic modal/overlay events
  document.addEventListener('ionModalDidPresent', modalHandler, true);
  document.addEventListener('ionModalWillPresent', modalHandler, true);
  document.addEventListener('ionActionSheetDidPresent', modalHandler, true);
  document.addEventListener('ionAlertDidPresent', modalHandler, true);
  document.addEventListener('ionAlertWillPresent', modalHandler, true);
  document.addEventListener('ionPopoverDidPresent', modalHandler, true);

  // Run immediately
  setTimeout(fixAriaIssues, 100);
  
  // Also run an immediate aggressive fix for current state
  setTimeout(() => {
    const currentFocused = document.activeElement;
    if (currentFocused) {
      const hiddenPage = currentFocused.closest('.ion-page-hidden[aria-hidden="true"], .ion-page.ion-page-hidden[aria-hidden="true"]');
      if (hiddenPage && currentFocused instanceof HTMLElement && currentFocused.blur) {
        currentFocused.blur();
        console.log('ARIA: Initial aggressive blur of focused element in hidden page:', currentFocused.tagName);
      }
    }
  }, 50);

  // Return cleanup function
  return () => {
    mutationObserver.disconnect();
    document.removeEventListener('focusin', focusHandler, true);
    document.removeEventListener('focusout', focusHandler, true);
    document.removeEventListener('ionModalDidPresent', modalHandler, true);
    document.removeEventListener('ionModalWillPresent', modalHandler, true);
    document.removeEventListener('ionActionSheetDidPresent', modalHandler, true);
    document.removeEventListener('ionAlertDidPresent', modalHandler, true);
    document.removeEventListener('ionAlertWillPresent', modalHandler, true);
    document.removeEventListener('ionPopoverDidPresent', modalHandler, true);
  };
};

// Export a manual trigger function
export const triggerAriaFix = () => {
  // Handle incorrect aria-modal on popovers (should only be on true modals)
  const popoversWithModal = window.document.querySelectorAll('ion-popover[aria-modal="true"]');
  popoversWithModal.forEach((popover) => {
    popover.removeAttribute('aria-modal');
    console.log('ARIA: Manual trigger removed incorrect aria-modal from popover');
  });
  
  const hiddenElements = window.document.querySelectorAll('[aria-hidden="true"]');
  
  hiddenElements.forEach((element) => {
    // Special handling for open modals/overlays - they should NOT have aria-hidden
    if (element.getAttribute('is-open') === 'true' || 
        element.classList.contains('popover-open') ||
        element.querySelector('.popover-open')) {
      
      // If it's an open modal/overlay with aria-hidden, remove aria-hidden
      if (element.tagName === 'ION-MODAL' || 
          element.tagName === 'ION-POPOVER' || 
          element.tagName === 'ION-ACTION-SHEET' ||
          element.tagName === 'ION-ALERT' ||
          element.classList.contains('modal-sheet') ||
          element.id?.includes('ion-overlay')) {
        element.removeAttribute('aria-hidden');
        console.log('ARIA: Manual trigger removed aria-hidden from open modal/overlay:', element.tagName);
      }
      return; // Skip further processing
    }
    
    // Special case: ION-ROUTER-OUTLET should NEVER have aria-hidden
    if (element.tagName === 'ION-ROUTER-OUTLET') {
      element.removeAttribute('aria-hidden');
      console.log('ARIA: Manual trigger removed aria-hidden from router outlet');
      return; // Don't blur focused elements in router outlet
    }
    
    if (element.tagName === 'ION-TAB' ||           
        element.tagName === 'ION-TABS' ||          
        element.tagName === 'ION-ACTION-SHEET' ||
        element.tagName === 'ION-MODAL' ||
        element.tagName === 'ION-POPOVER' ||
        element.tagName === 'ION-ALERT' ||
        element.tagName === 'ION-TOAST' ||
        element.classList.contains('ion-page') ||
        element.classList.contains('ion-page-hidden') ||
        element.classList.contains('tab-hidden') ||
        element.classList.contains('modal-sheet') ||
        element.id?.includes('ion-overlay')) {
      
      const focusedInside = element.querySelectorAll(':focus');
      focusedInside.forEach((el) => {
        if (el instanceof HTMLElement && el.blur) {
          el.blur();
        }
      });
    }
  });
};